/**
 * Sign-in flow — #mc-phase2 + ADR-0093.
 *
 * Multi-step state machine. ADR-0093 extended the original 3-kind union
 * (idle, awaiting-code, awaiting-passkey) to 7 kinds with the addition
 * of the first-sign-in chooser and SMS peer factor:
 *
 *   idle                       email-only form; submits → /api/auth/request
 *                              The /request response carries `nextStep`
 *                              and routes to the matching pane.
 *
 *   awaiting-code              6-digit OTP form; submits → /api/auth/verify
 *                              /verify response carries `hasCredentials`
 *                              and `promptFactorPicker`, which branch into:
 *                                hC=t, pFP=f → assert (existing passkey)
 *                                hC=t, pFP=t → assert + self-heal commit
 *                                hC=f, pFP=t → chooser (first sign-in)
 *                                hC=f, pFP=f → register (defensive)
 *
 *   awaiting-passkey           Register-or-assert pane; on register success
 *                              we commit `strategy='passkey'`; on assert
 *                              with `needsStrategyHeal`, same silent commit
 *                              (ADR-0093 §6 self-heal).
 *
 *   awaiting-strategy-choice   Chooser: passkey (recommended) | SMS.
 *
 *   awaiting-sms-enroll-phone  Phone form; submits → /api/auth/phone/register-begin
 *   awaiting-sms-enroll-code   6-digit SMS code; submits → /api/auth/phone/register-finish
 *                              + /api/auth/strategy { strategy: 'sms' }
 *
 *   awaiting-sms-code          Subsequent SMS sign-in (committed SMS users
 *                              who already enrolled). Submits → /api/auth/sms/verify.
 *
 * Persistence: sessionStorage (per-tab). The SMS code flow inherently
 * requires a tab switch to read the SMS, the same as email-OTP today;
 * persistence is essential, not nice-to-have.
 *
 * Error display: ClientError surfaces a `reason` string. We map the
 * known reasons to user-friendly copy; unknown reasons fall through to
 * a generic "Something went wrong" message.
 */

import { useEffect, useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ClientError, UnauthenticatedError } from "@/lib/api";
import {
  useCommitAuthStrategy,
  useEnrollPasskey,
  useRegisterPhoneBegin,
  useRegisterPhoneFinish,
  useRequestOtp,
  useSignInWithPasskey,
  useVerifyOtp,
  useVerifySmsSignIn,
} from "@/lib/use-sign-in";

type Step =
  | { kind: "idle" }
  | { kind: "awaiting-code"; email: string }
  | {
      kind: "awaiting-passkey";
      email: string;
      mode: "register" | "assert";
      /**
       * Set when we entered this step from /verify with
       * `hasCredentials=true && promptFactorPicker=true` — i.e. the
       * self-heal path. On successful assert, silently call
       * /api/auth/strategy {passkey}. Default false.
       */
      needsStrategyHeal?: boolean;
    }
  | { kind: "awaiting-strategy-choice"; email: string }
  | { kind: "awaiting-sms-enroll-phone"; email: string }
  | { kind: "awaiting-sms-enroll-code"; email: string }
  | { kind: "awaiting-sms-code"; email: string };

const STEP_KEY = "mc.signin.step";
const EMAIL_KEY = "mc.signin.email";

function readPersistedStep(): Step {
  try {
    const raw = sessionStorage.getItem(STEP_KEY);
    if (!raw) return { kind: "idle" };
    const parsed = JSON.parse(raw) as Step;
    if (parsed.kind === "awaiting-code" && typeof parsed.email === "string")
      return parsed;
    if (
      parsed.kind === "awaiting-passkey" &&
      typeof parsed.email === "string" &&
      (parsed.mode === "register" || parsed.mode === "assert")
    )
      return parsed;
    if (
      parsed.kind === "awaiting-strategy-choice" &&
      typeof parsed.email === "string"
    )
      return parsed;
    if (
      parsed.kind === "awaiting-sms-enroll-phone" &&
      typeof parsed.email === "string"
    )
      return parsed;
    if (
      parsed.kind === "awaiting-sms-enroll-code" &&
      typeof parsed.email === "string"
    )
      return parsed;
    if (
      parsed.kind === "awaiting-sms-code" &&
      typeof parsed.email === "string"
    )
      return parsed;
    return { kind: "idle" };
  } catch {
    return { kind: "idle" };
  }
}

function readPersistedEmail(): string {
  try {
    return sessionStorage.getItem(EMAIL_KEY) ?? "";
  } catch {
    return "";
  }
}

function persist(step: Step, email: string): void {
  try {
    if (step.kind === "idle") {
      sessionStorage.removeItem(STEP_KEY);
    } else {
      sessionStorage.setItem(STEP_KEY, JSON.stringify(step));
    }
    if (email) sessionStorage.setItem(EMAIL_KEY, email);
    else sessionStorage.removeItem(EMAIL_KEY);
  } catch {
    // Storage unavailable (private mode quota, disabled) — degrade to
    // in-memory-only state; the form just won't survive a remount.
  }
}

function clearPersisted(): void {
  try {
    sessionStorage.removeItem(STEP_KEY);
    sessionStorage.removeItem(EMAIL_KEY);
  } catch {
    /* ignore */
  }
}

function reasonOf(err: unknown): string | undefined {
  if (err instanceof ClientError) return err.reason;
  if (err instanceof UnauthenticatedError) return err.reason;
  return undefined;
}

function reasonToMessage(err: unknown): string {
  const reason = reasonOf(err);
  switch (reason) {
    case "bad_code":
      return "That code is wrong. Try again, or request a new one.";
    case "code_expired":
      return "That code has expired. Request a new one.";
    case "not_allowed":
      return "This account isn't an authorized operator.";
    case "invalid_email":
      return "Enter a valid email address.";
    case "invalid_phone":
      return "Enter a 10-digit US phone number.";
    case "invalid_body":
      return "Something about that request was malformed. Try again.";
    case "no_credentials":
      return "No passkey is on file for this account. Request a code to enroll one.";
    case "verification_failed":
      return "Passkey verification failed. Try again.";
    case "credential_mismatch":
      return "That passkey doesn't belong to this account.";
    case "unknown_credential":
      return "That passkey isn't recognized. It may have been revoked.";
    case "no_enrollment":
      return "Your sign-in window expired. Start over.";
    case "no_verified_phone":
      return "No phone is on file for this account. Set one up first.";
    case "sms_failed":
      return "We couldn't send the SMS. Try again in a minute.";
    case "sms_no_phone":
      return "SMS sign-in is selected but no phone is on file. Contact an admin.";
    default:
      break;
  }
  if (reason) return `Sign-in failed (${reason}).`;
  if (err instanceof Error) return err.message || "Something went wrong.";
  return "Something went wrong.";
}

export function SignInPage() {
  const [step, setStep] = useState<Step>(readPersistedStep);
  const [email, setEmail] = useState(readPersistedEmail);
  const [code, setCode] = useState("");
  const [phone, setPhone] = useState("");

  useEffect(() => {
    persist(step, email);
  }, [step, email]);

  const requestOtp = useRequestOtp();
  const verifyOtp = useVerifyOtp();
  const enrollPasskey = useEnrollPasskey();
  const signInWithPasskey = useSignInWithPasskey();
  const registerPhoneBegin = useRegisterPhoneBegin();
  const registerPhoneFinish = useRegisterPhoneFinish();
  const commitAuthStrategy = useCommitAuthStrategy();
  const verifySmsSignIn = useVerifySmsSignIn();

  // ----- Step 1: email submit -----
  const onSubmitEmail = async (ev: FormEvent) => {
    ev.preventDefault();
    if (!email.trim()) return;
    try {
      const result = await requestOtp.mutateAsync({ email: email.trim() });
      // ADR-0093 — branch on the operator's committed strategy.
      if (result.nextStep === "passkey") {
        setStep({
          kind: "awaiting-passkey",
          email: email.trim(),
          mode: "assert",
        });
      } else if (result.nextStep === "sms") {
        setStep({ kind: "awaiting-sms-code", email: email.trim() });
      } else {
        setStep({ kind: "awaiting-code", email: email.trim() });
      }
      setCode("");
    } catch {
      // Error surfaces via requestOtp.error in the JSX.
    }
  };

  // ----- Step 2: code submit -----
  const onSubmitCode = async (ev: FormEvent) => {
    ev.preventDefault();
    if (step.kind !== "awaiting-code") return;
    if (!/^\d{6}$/.test(code)) return;
    try {
      const result = await verifyOtp.mutateAsync({ email: step.email, code });
      // ADR-0093 — four legitimate post-/verify branches.
      if (result.hasCredentials && !result.promptFactorPicker) {
        setStep({
          kind: "awaiting-passkey",
          email: step.email,
          mode: "assert",
        });
      } else if (result.hasCredentials && result.promptFactorPicker) {
        // Self-heal: credential exists but strategy never committed.
        // Route straight to assertion; on success, silently commit.
        setStep({
          kind: "awaiting-passkey",
          email: step.email,
          mode: "assert",
          needsStrategyHeal: true,
        });
      } else if (!result.hasCredentials && result.promptFactorPicker) {
        setStep({ kind: "awaiting-strategy-choice", email: step.email });
      } else {
        // Defensive: !hasCredentials && !promptFactorPicker means
        // strategy committed but no credential — only legitimate for
        // strategy='sms'. The /request path should have skipped /verify
        // for that case; treat as "register a passkey" so we don't
        // strand the operator.
        setStep({
          kind: "awaiting-passkey",
          email: step.email,
          mode: "register",
        });
      }
    } catch {
      // Error surfaces via verifyOtp.error in the JSX.
    }
  };

  // ----- Step 3a: register a passkey -----
  const onEnrollPasskey = async () => {
    if (step.kind !== "awaiting-passkey") return;
    try {
      await enrollPasskey.mutateAsync({});
      // Commit the chooser's choice. If this fails (race), the next
      // sign-in's self-heal path will converge. We don't surface the
      // commit error because the user IS signed in at this point — the
      // session cookie was minted by /passkey/register/finish.
      try {
        await commitAuthStrategy.mutateAsync({ strategy: "passkey" });
      } catch (err) {
        console.warn("sign-in: passkey strategy commit failed (self-heal on next sign-in)", err);
      }
      clearPersisted();
    } catch {
      // Error surfaces via enrollPasskey.error in the JSX.
    }
  };

  // ----- Step 3b: sign in with an existing passkey -----
  const onAssertPasskey = async () => {
    if (step.kind !== "awaiting-passkey") return;
    const heal = step.needsStrategyHeal === true;
    try {
      await signInWithPasskey.mutateAsync({ email: step.email });
      if (heal) {
        // Self-heal: a credential exists but strategy was uncommitted.
        // Silently commit now that we've proved control of the
        // credential. Same swallow-error posture as the register path.
        try {
          await commitAuthStrategy.mutateAsync({ strategy: "passkey" });
        } catch (err) {
          console.warn("sign-in: passkey self-heal commit failed", err);
        }
      }
      clearPersisted();
    } catch {
      // Error surfaces via signInWithPasskey.error in the JSX.
    }
  };

  // ----- Chooser -----
  const onChoosePasskey = () => {
    if (step.kind !== "awaiting-strategy-choice") return;
    setStep({ kind: "awaiting-passkey", email: step.email, mode: "register" });
  };
  const onChooseSms = () => {
    if (step.kind !== "awaiting-strategy-choice") return;
    setStep({ kind: "awaiting-sms-enroll-phone", email: step.email });
    setPhone("");
  };

  // ----- SMS enroll: phone submit -----
  const onSubmitSmsEnrollPhone = async (ev: FormEvent) => {
    ev.preventDefault();
    if (step.kind !== "awaiting-sms-enroll-phone") return;
    if (!phone.trim()) return;
    try {
      await registerPhoneBegin.mutateAsync({ phone: phone.trim() });
      setStep({ kind: "awaiting-sms-enroll-code", email: step.email });
      setCode("");
    } catch {
      // Error surfaces via registerPhoneBegin.error in the JSX.
    }
  };

  // ----- SMS enroll: code submit -----
  const onSubmitSmsEnrollCode = async (ev: FormEvent) => {
    ev.preventDefault();
    if (step.kind !== "awaiting-sms-enroll-code") return;
    if (!/^\d{6}$/.test(code)) return;
    try {
      await registerPhoneFinish.mutateAsync({ code });
      // Phone proved; commit the SMS strategy. Failure here strands the
      // operator at strategy='email-only' with a verified phone — the
      // next sign-in's chooser will surface and let them retry.
      await commitAuthStrategy.mutateAsync({ strategy: "sms" });
      clearPersisted();
    } catch {
      // Error surfaces via registerPhoneFinish.error / commitAuthStrategy.error.
    }
  };

  // ----- Subsequent SMS sign-in: code submit -----
  const onSubmitSmsSignInCode = async (ev: FormEvent) => {
    ev.preventDefault();
    if (step.kind !== "awaiting-sms-code") return;
    if (!/^\d{6}$/.test(code)) return;
    try {
      await verifySmsSignIn.mutateAsync({ email: step.email, code });
      clearPersisted();
    } catch {
      // Error surfaces via verifySmsSignIn.error in the JSX.
    }
  };

  const onReset = () => {
    clearPersisted();
    setStep({ kind: "idle" });
    setCode("");
    setPhone("");
  };

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center gap-6 p-8"
      data-testid="sign-in-page"
    >
      <div className="flex flex-col items-center gap-2">
        <h1 className="text-2xl">Command Center</h1>
        <p className="text-sm text-plane-muted">Sign in to continue</p>
      </div>

      {/* White card floating on the plane (ADR-0136) — bg-card restores the
          light panel the old flat-gray theme showed by default; text-card-foreground
          keeps its interior navy on the navy ground. */}
      <div className="w-full max-w-sm rounded-lg border bg-card text-card-foreground p-6 shadow-sm">
        {step.kind === "idle" && (
          <form
            onSubmit={onSubmitEmail}
            className="flex flex-col gap-4"
            data-testid="sign-in-email-form"
          >
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">Email</span>
              <Input
                type="email"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@legalflare.com"
                data-testid="sign-in-email-input"
              />
            </label>
            <Button
              type="submit"
              variant="cta"
              disabled={requestOtp.isPending || !email.trim()}
              data-testid="sign-in-request-code-button"
            >
              {requestOtp.isPending ? "Logging in…" : "Log in"}
            </Button>
            {requestOtp.error && (
              <p
                className="text-sm text-error"
                role="alert"
                data-testid="sign-in-error"
              >
                {reasonToMessage(requestOtp.error)}
              </p>
            )}
          </form>
        )}

        {step.kind === "awaiting-code" && (
          <form
            onSubmit={onSubmitCode}
            className="flex flex-col gap-4"
            data-testid="sign-in-code-form"
          >
            <p className="text-sm text-muted-foreground">
              We sent a 6-digit code to{" "}
              <strong className="font-medium">{step.email}</strong>. It expires in
              15 minutes.
            </p>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">Code</span>
              <Input
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                autoFocus
                required
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="000000"
                data-testid="sign-in-code-input"
              />
            </label>
            <Button
              type="submit"
              variant="cta"
              disabled={verifyOtp.isPending || !/^\d{6}$/.test(code)}
              data-testid="sign-in-verify-code-button"
            >
              {verifyOtp.isPending ? "Verifying…" : "Verify code"}
            </Button>
            {verifyOtp.error && (
              <p
                className="text-sm text-error"
                role="alert"
                data-testid="sign-in-error"
              >
                {reasonToMessage(verifyOtp.error)}
              </p>
            )}
            <button
              type="button"
              onClick={onReset}
              className="text-xs text-muted-foreground underline-offset-2 hover:underline"
              data-testid="sign-in-reset"
            >
              Use a different email
            </button>
          </form>
        )}

        {step.kind === "awaiting-strategy-choice" && (
          <div
            className="flex flex-col gap-4"
            data-testid="sign-in-chooser"
          >
            <h2 className="text-lg font-sans font-semibold">Pick a sign-in factor</h2>
            <p className="text-sm text-muted-foreground">
              You can sign in with a passkey (recommended) or with an SMS code
              sent to your phone. You only pick once — every sign-in after
              that uses the factor you choose now.
            </p>
            <Button
              onClick={onChoosePasskey}
              variant="cta"
              data-testid="sign-in-choose-passkey"
            >
              Set up a passkey (recommended)
            </Button>
            <Button
              onClick={onChooseSms}
              variant="secondary"
              data-testid="sign-in-choose-sms"
            >
              Use SMS instead
            </Button>
            <button
              type="button"
              onClick={onReset}
              className="text-xs text-muted-foreground underline-offset-2 hover:underline"
              data-testid="sign-in-reset"
            >
              Use a different email
            </button>
          </div>
        )}

        {step.kind === "awaiting-passkey" && step.mode === "register" && (
          <div
            className="flex flex-col gap-4"
            data-testid="sign-in-register-passkey"
          >
            <h2 className="text-lg font-sans font-semibold">Set up a passkey</h2>
            <p className="text-sm text-muted-foreground">
              Add a passkey on this device — Touch ID, Windows Hello, or a
              hardware key. You'll use it to sign in from now on.
            </p>
            <Button
              onClick={onEnrollPasskey}
              variant="cta"
              disabled={enrollPasskey.isPending || commitAuthStrategy.isPending}
              data-testid="sign-in-enroll-passkey-button"
            >
              {enrollPasskey.isPending ? "Setting up…" : "Set up passkey"}
            </Button>
            {enrollPasskey.error && (
              <p
                className="text-sm text-error"
                role="alert"
                data-testid="sign-in-error"
              >
                {reasonToMessage(enrollPasskey.error)}
              </p>
            )}
            <button
              type="button"
              onClick={onReset}
              className="text-xs text-muted-foreground underline-offset-2 hover:underline"
              data-testid="sign-in-reset"
            >
              Cancel
            </button>
          </div>
        )}

        {step.kind === "awaiting-passkey" && step.mode === "assert" && (
          <div
            className="flex flex-col gap-4"
            data-testid="sign-in-assert-passkey"
          >
            <h2 className="text-lg font-sans font-semibold">Use your passkey</h2>
            <p className="text-sm text-muted-foreground">
              Use the passkey on this device to finish signing in.
            </p>
            <Button
              onClick={onAssertPasskey}
              variant="cta"
              disabled={signInWithPasskey.isPending}
              data-testid="sign-in-assert-passkey-button"
            >
              {signInWithPasskey.isPending ? "Signing in…" : "Use passkey"}
            </Button>
            {signInWithPasskey.error && (
              <p
                className="text-sm text-error"
                role="alert"
                data-testid="sign-in-error"
              >
                {reasonToMessage(signInWithPasskey.error)}
              </p>
            )}
            <button
              type="button"
              onClick={onReset}
              className="text-xs text-muted-foreground underline-offset-2 hover:underline"
              data-testid="sign-in-reset"
            >
              Cancel
            </button>
          </div>
        )}

        {step.kind === "awaiting-sms-enroll-phone" && (
          <form
            onSubmit={onSubmitSmsEnrollPhone}
            className="flex flex-col gap-4"
            data-testid="sign-in-sms-phone-form"
          >
            <h2 className="text-lg font-sans font-semibold">Add your phone</h2>
            <p className="text-sm text-muted-foreground">
              We'll text a 6-digit code to confirm. US numbers only.
            </p>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">Phone number</span>
              <Input
                type="tel"
                autoFocus
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 555-0192"
                data-testid="sign-in-sms-phone-input"
              />
            </label>
            <Button
              type="submit"
              variant="cta"
              disabled={registerPhoneBegin.isPending || !phone.trim()}
              data-testid="sign-in-sms-phone-submit"
            >
              {registerPhoneBegin.isPending ? "Sending…" : "Send SMS code"}
            </Button>
            {registerPhoneBegin.error && (
              <p
                className="text-sm text-error"
                role="alert"
                data-testid="sign-in-error"
              >
                {reasonToMessage(registerPhoneBegin.error)}
              </p>
            )}
            <button
              type="button"
              onClick={onReset}
              className="text-xs text-muted-foreground underline-offset-2 hover:underline"
              data-testid="sign-in-reset"
            >
              Cancel
            </button>
          </form>
        )}

        {step.kind === "awaiting-sms-enroll-code" && (
          <form
            onSubmit={onSubmitSmsEnrollCode}
            className="flex flex-col gap-4"
            data-testid="sign-in-sms-enroll-code-form"
          >
            <p className="text-sm text-muted-foreground">
              Enter the 6-digit code we just texted you.
            </p>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">SMS code</span>
              <Input
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                autoFocus
                required
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="000000"
                data-testid="sign-in-sms-enroll-code-input"
              />
            </label>
            <Button
              type="submit"
              variant="cta"
              disabled={
                registerPhoneFinish.isPending ||
                commitAuthStrategy.isPending ||
                !/^\d{6}$/.test(code)
              }
              data-testid="sign-in-sms-enroll-code-submit"
            >
              {registerPhoneFinish.isPending || commitAuthStrategy.isPending
                ? "Verifying…"
                : "Verify code"}
            </Button>
            {(registerPhoneFinish.error || commitAuthStrategy.error) && (
              <p
                className="text-sm text-error"
                role="alert"
                data-testid="sign-in-error"
              >
                {reasonToMessage(
                  registerPhoneFinish.error ?? commitAuthStrategy.error,
                )}
              </p>
            )}
            <button
              type="button"
              onClick={onReset}
              className="text-xs text-muted-foreground underline-offset-2 hover:underline"
              data-testid="sign-in-reset"
            >
              Cancel
            </button>
          </form>
        )}

        {step.kind === "awaiting-sms-code" && (
          <form
            onSubmit={onSubmitSmsSignInCode}
            className="flex flex-col gap-4"
            data-testid="sign-in-sms-code-form"
          >
            <p className="text-sm text-muted-foreground">
              We texted a 6-digit code to your phone on file.
            </p>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">SMS code</span>
              <Input
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                autoFocus
                required
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="000000"
                data-testid="sign-in-sms-code-input"
              />
            </label>
            <Button
              type="submit"
              variant="cta"
              disabled={verifySmsSignIn.isPending || !/^\d{6}$/.test(code)}
              data-testid="sign-in-sms-code-submit"
            >
              {verifySmsSignIn.isPending ? "Verifying…" : "Verify code"}
            </Button>
            {verifySmsSignIn.error && (
              <p
                className="text-sm text-error"
                role="alert"
                data-testid="sign-in-error"
              >
                {reasonToMessage(verifySmsSignIn.error)}
              </p>
            )}
            <button
              type="button"
              onClick={onReset}
              className="text-xs text-muted-foreground underline-offset-2 hover:underline"
              data-testid="sign-in-reset"
            >
              Use a different email
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
